"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import { useAuth } from "../../services/AuthContext"
import { api, apiVentas } from "../../services/Axios"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import Loader from "../utils/loader/Loader"
import PaymentModal from "./PaymentModal"
import WeightModal from "./WeightModal"
import PrintModal from "../utils/print/PrintModal"
import CreateOrderModal from "../utils/modals/CreateOrderModal"
import "@fortawesome/fontawesome-free/css/all.min.css"
import { InvoiceRecipientInfo } from "../utils/modals/SendInvoiceModal";
import QRScanner from "../productos/QRScanner"
import VariosModal from "../caja/VariosModal"
import QuickEditProductModal from "./QuickEditProductModal"

// --- Interfaces y funciones iniciales ---

interface SelectedProduct {
  producto: Product
  cantidad: string | number
  descuento?: number | string
}
interface Product {
  _id: string
  codigo?: string
  nombre: string
  cantidad?: number
  unidadMedida?: "unidad" | "kg" | "gramo" | "litro" | "metro"
  umbral?: number
  fechaCaducidad?: Date
  categoria?: { _id: string; nombre: string }
  sucursal?: { _id: string; nombre: string }
  marca?: { _id: string; nombre: string }
  proveedor?: { _id: string; nombre: string }
  referenciaProveedor?: string
  valorVenta: number
  valorVentaTotal: number
  valorCompra: number
  ivaPorcentaje?: number
  margenGanancia: number
  tipoCalculo?: 'recargo' | 'margen'
  organization?: string
  esVarios?: boolean
}

interface Order {
  id: string
  name: string
  selectedProducts: SelectedProduct[]
  subtotal: number
  iva: number
  dineroRecibido: string | number
  metodoPago: string
  descuentoPorcentaje: string | number
}

interface Sucursal {
  _id: string
  nombre: string
}

const createPrincipalOrder = (): Order => ({
  id: `principal-${Date.now()}`,
  name: "Principal",
  selectedProducts: [],
  subtotal: 0,
  iva: 0,
  dineroRecibido: "",
  metodoPago: "efectivo",
  descuentoPorcentaje: "",
})

// --- Componente principal ---

function ManageSales() {
  // --- Estados (sin cambios) ---
  const { userData } = useAuth()
  const [orders, setOrders] = useState<Order[]>([createPrincipalOrder()])
  const [activeOrderId, setActiveOrderId] = useState<string | null>(orders[0].id)
  const [isNameModalOpen, setIsNameModalOpen] = useState(false)
  const [productos, setProductos] = useState<Product[]>([])
  const [filteredProductos, setFilteredProductos] = useState<Product[]>([])
  const [sucursales, setSucursales] = useState<Sucursal[]>([])
  const [selectedSucursal, setSelectedSucursal] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false)
  const [messageContent, setMessageContent] = useState("")
  const [messageType, setMessageType] = useState<"success" | "error" | "info" | "warning" | "aviso">("success")
  const [isRightPaneVisible, setIsRightPaneVisible] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false)
  const [factura, setFactura] = useState<any>(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isPesoModalOpen, setIsPesoModalOpen] = useState(false)
  const [selectedPesoProduct, setSelectedPesoProduct] = useState<Product | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const activeOrder = orders.find((o) => o.id === activeOrderId)
  const activeOrderIndex = orders.findIndex((o) => o.id === activeOrderId)

  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false)

  const [cajaAbierta, setCajaAbierta] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cajaActiva, setCajaActiva] = useState<any>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const [isVariosModalOpen, setIsVariosModalOpen] = useState(false)

  const [isQuickEditModalOpen, setIsQuickEditModalOpen] = useState(false)
  const [productToEdit, setProductToEdit] = useState<{
    _id: string;
    nombre: string;
    unidadMedida: "unidad" | "kg" | "gramo" | "litro" | "metro";
    cantidad: number;
    valorCompra: number;
    margenGanancia: number;
    valorVentaTotal: number;
    ivaPorcentaje?: number;
    esVarios?: boolean;
  } | null>(null)

  // --- Funciones memoizadas (sin cambios) ---
  const updateActiveOrder = useCallback((updatedOrderData: Partial<Order>) => {
    if (activeOrderIndex === -1) return
    setOrders(prevOrders => {
        const newOrders = [...prevOrders]
        newOrders[activeOrderIndex] = { ...newOrders[activeOrderIndex], ...updatedOrderData }
        return newOrders
    })
  }, [activeOrderIndex])

  const updateSubtotal = useCallback((
    products: SelectedProduct[],
    callback: (newSubtotal: number, newIva: number) => void
  ) => {
    let newSubtotal = 0
    let newIva = 0

    products.forEach((prod) => {
      const cantidad = Number(prod.cantidad) || 0
      const baseTotal = cantidad * prod.producto.valorVentaTotal
      const descuento = Number(prod.descuento) || 0
      const valorConDescuento = baseTotal * (1 - descuento / 100)

      if (userData?.organization?.responsableIVA) {
        const sinIVA = valorConDescuento / (1 + (prod.producto.ivaPorcentaje || 0) / 100)
        newSubtotal += sinIVA
        newIva += valorConDescuento - sinIVA
      } else {
        newSubtotal += valorConDescuento
      }
    })

    callback(newSubtotal, newIva)
  }, [userData])

  const handleSaveNewOrder = useCallback((orderName: string) => {
    const newOrder: Order = {
      id: Date.now().toString(),
      name: orderName,
      selectedProducts: [],
      subtotal: 0,
      iva: 0,
      dineroRecibido: "",
      metodoPago: "efectivo",
      descuentoPorcentaje: "",
    }
    setOrders(prevOrders => [...prevOrders, newOrder])
    setActiveOrderId(newOrder.id)
    setIsNameModalOpen(false)
  }, [])

  const closeOrder = useCallback((orderIdToClose: string) => {
    const orderIndexToClose = orders.findIndex((o) => o.id === orderIdToClose)
    const updatedOrders = orders.filter((o) => o.id !== orderIdToClose)
    if (updatedOrders.length === 0) {
      const newPrincipal = createPrincipalOrder()
      setOrders([newPrincipal])
      setActiveOrderId(newPrincipal.id)
      return
    }
    setOrders(updatedOrders)
    if (activeOrderId === orderIdToClose) {
      const newActiveIndex = Math.max(0, orderIndexToClose - 1)
      setActiveOrderId(updatedOrders[newActiveIndex].id)
    }
  }, [activeOrderId, orders])

  const handleClearProducts = useCallback(() => {
    if (!activeOrder) return
    updateActiveOrder({ selectedProducts: [], subtotal: 0, iva: 0 })
  }, [activeOrder, updateActiveOrder])

  const openPaymentModal = useCallback(() => {
    if (!activeOrder) return
    updateActiveOrder({ dineroRecibido: "", descuentoPorcentaje: "" })
    setIsPaymentModalOpen(true)
  }, [activeOrder, updateActiveOrder])

  // --- CAMBIO: Variable para detectar si CUALQUIER modal está abierto ---
  const isAnyModalOpen =
    isNameModalOpen ||
    isPaymentModalOpen ||
    isPesoModalOpen ||
    isConfirmationModalOpen ||
    isMessageModalOpen ||
    isPrintModalOpen ||
    isVariosModalOpen ||
    isQuickEditModalOpen

  // --- useEffects para atajos de teclado (con correcciones) ---

  useEffect(() => {
    const verificarCajaAbierta = async () => {
      setIsInitializing(true);
      try {
        const response = await api.get('/api/cash-register/active');
        setCajaAbierta(true);
        setCajaActiva(response.data.caja);
        
        if (response.data.caja?.sucursal?._id) {
          setSelectedSucursal(response.data.caja.sucursal._id);
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          setCajaAbierta(false);
          setCajaActiva(null);
          setSelectedSucursal(null);
        }
      } finally {
        setIsInitializing(false);
      }
    };
    
    verificarCajaAbierta();
  }, []);

  useEffect(() => {
    if (!isPaymentModalOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isPaymentModalOpen])

  useEffect(() => {
    if (!isLoading && selectedSucursal && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [filteredProductos, isLoading, selectedSucursal])

  useEffect(() => {
    const handleFnKey = (e: KeyboardEvent) => {
      // CAMBIO: No hacer nada si un modal está abierto
      if (isAnyModalOpen) return

      if (e.key === "F8") {
        e.preventDefault()
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }
    }
    window.addEventListener("keydown", handleFnKey)
    return () => {
      window.removeEventListener("keydown", handleFnKey)
    }
  }, [isAnyModalOpen]) // CAMBIO: Añadir la dependencia

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // No hacer nada si un modal está abierto
      if (isAnyModalOpen) return

      // No hacer nada si estás escribiendo en un input o textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === "Escape") {
        handleClearProducts()
      } else if (e.shiftKey && activeOrder && activeOrder.selectedProducts.length > 0) {
        e.preventDefault()
        openPaymentModal()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [activeOrder, isAnyModalOpen, handleClearProducts, openPaymentModal])

  // (El resto del componente no tiene más cambios)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        if (!document.activeElement?.closest(".carrito-descuento-activo")) {
          setIsRightPaneVisible(false)
        }
      }
    }
    window.addEventListener("resize", handleResize)
    handleResize()
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    // 🔥 Esperar a que termine la inicialización
    if (isInitializing) return;
    
    setIsLoading(true)
    api
      .get("/api/sucursales")
      .then((response) => {
        const sucursalesData = response.data.sucursales || response.data
        if (Array.isArray(sucursalesData)) {
          setSucursales(sucursalesData)
          
          // Solo establecer sucursal si NO hay caja abierta
          if (!cajaActiva && !cajaAbierta && sucursalesData.length > 0) {
            setSelectedSucursal(sucursalesData[0]._id);
          }
        } else {
          setSucursales([])
        }
      })
      .catch((error) => {
        console.error("Error al cargar sucursales:", error)
        setMessageContent("Error al cargar las sucursales")
        setMessageType("error")
        setIsMessageModalOpen(true)
      })
      .finally(() => setIsLoading(false))
  }, [cajaActiva, cajaAbierta, isInitializing])

  useEffect(() => {
    // 🔥 NO cargar productos hasta que termine la inicialización
    if (isInitializing) return;
    
    if (selectedSucursal) {
      setIsLoading(true)
      api
        .get(`/api/productos?sucursal=${selectedSucursal}&limit=10`)
        .then((response) => {
          const productosData = response.data.productos || []
          
          // NUEVO: Crear el producto "Varios" y agregarlo al inicio
          const productoVarios: Product = {
            _id: 'varios-especial',
            nombre: 'Varios',
            codigo: 'VARIOS',
            valorVentaTotal: 0,
            valorCompra: 0,
            cantidad: 999999,
            unidadMedida: 'unidad',
            ivaPorcentaje: 0,
            margenGanancia: 0,
            valorVenta: 0,
            organization: userData?.organization?._id || '',
            marca: { nombre: 'Venta Rápida', _id: 'varios' } as any,
          } as any as Product;
          
          const todosLosProductos = [productoVarios, ...(Array.isArray(productosData) ? productosData : [])];
          
          setProductos(todosLosProductos)
          setFilteredProductos(todosLosProductos)
        })
        .catch((error) => console.error("Error al obtener productos:", error))
        .finally(() => setIsLoading(false))
    }
  }, [selectedSucursal, isInitializing, userData])

  const toggleRightPane = () => {
    setIsRightPaneVisible((prev) => !prev)
  }

  const handleSucursalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Si hay caja abierta, no permitir cambio de sucursal
    if (cajaAbierta) {
      setMessageContent("No puedes cambiar de sucursal mientras tengas una caja abierta. Cierra la caja primero.");
      setMessageType("warning");
      setIsMessageModalOpen(true);
      return;
    }

    const newSucursalId = e.target.value
    setSelectedSucursal(newSucursalId)

    const newPrincipal = createPrincipalOrder()
    setOrders([newPrincipal])
    setActiveOrderId(newPrincipal.id)

    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

    const handleQuickEdit = (product: Product) => {
    // Convertir al formato esperado por el modal
    const productForEdit = {
      _id: product._id,
      nombre: product.nombre,
      unidadMedida: product.unidadMedida || "unidad",
      cantidad: product.cantidad || 0,
      valorCompra: product.valorCompra,
      margenGanancia: product.margenGanancia,
      valorVentaTotal: product.valorVentaTotal,
      ivaPorcentaje: product.ivaPorcentaje,
      tipoCalculo: product.tipoCalculo ?? 'recargo',
      esVarios: product.esVarios,
    };
    
    setProductToEdit(productForEdit);
    setIsQuickEditModalOpen(true);
  }

  const handleQuickEditSuccess = useCallback(() => {
    // Refrescar SOLO el producto editado específico
    if (selectedSucursal && productToEdit) {
      // Traer el producto específico actualizado por su ID
      api
        .get(`/api/productos/${productToEdit._id}`)
        .then((response) => {
          const productoActualizado = response.data;
          
          if (!productoActualizado) {
            setMessageContent("No se pudo encontrar el producto actualizado")
            setMessageType("error")
            setIsMessageModalOpen(true)
            return;
          }
          
          // Actualizar en el grid de productos (solo el específico)
          setProductos(prevProductos => 
            prevProductos.map(p => 
              p._id === productoActualizado._id ? productoActualizado : p
            )
          );
          
          setFilteredProductos(prevFiltered => 
            prevFiltered.map(p => 
              p._id === productoActualizado._id ? productoActualizado : p
            )
          );

          // Actualizar producto en TODOS los carritos donde aparezca
          setOrders(prevOrders => 
            prevOrders.map(order => {
              const hasProduct = order.selectedProducts.some(
                sp => sp.producto._id === productoActualizado._id
              );
              
              if (!hasProduct) return order;

              const updatedProducts = order.selectedProducts.map((sp) => {
                if (sp.producto._id === productoActualizado._id) {
                  return {
                    ...sp,
                    producto: productoActualizado
                  };
                }
                return sp;
              });

              // Recalcular subtotal e IVA para esta orden
              let newSubtotal = 0;
              let newIva = 0;

              updatedProducts.forEach((prod) => {
                const cantidad = Number(prod.cantidad) || 0;
                const baseTotal = cantidad * prod.producto.valorVentaTotal;
                const descuento = Number(prod.descuento) || 0;
                const valorConDescuento = baseTotal * (1 - descuento / 100);

                if (userData?.organization?.responsableIVA) {
                  const sinIVA = valorConDescuento / (1 + (prod.producto.ivaPorcentaje || 0) / 100);
                  newSubtotal += sinIVA;
                  newIva += valorConDescuento - sinIVA;
                } else {
                  newSubtotal += valorConDescuento;
                }
              });

              return {
                ...order,
                selectedProducts: updatedProducts,
                subtotal: newSubtotal,
                iva: newIva
              };
            })
          );

          setMessageContent("Producto actualizado exitosamente. Los cambios se reflejaron en todos los carritos.")
          setMessageType("success")
          setIsMessageModalOpen(true)
        })
        .catch((error) => {
          console.error("Error al refrescar producto:", error)
          setMessageContent("Error al actualizar el producto en el carrito")
          setMessageType("error")
          setIsMessageModalOpen(true)
        })
    }
  }, [selectedSucursal, productToEdit, userData])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase()
    setSearchTerm(searchValue)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (!selectedSucursal) return

      if (searchValue.trim() === "") {
        api
          .get(`/api/productos?sucursal=${selectedSucursal}&limit=10`)
          .then((res) => {
            const productosData: Product[] = res.data.productos || []
            
            // AGREGAR: Incluir "Varios" cuando se limpia la búsqueda
            const productoVarios: Product = {
              _id: 'varios-especial',
              nombre: 'Varios',
              codigo: 'VARIOS',
              valorVentaTotal: 0,
              valorCompra: 0,
              cantidad: 999999,
              unidadMedida: 'unidad',
              ivaPorcentaje: 0,
              margenGanancia: 0,
              valorVenta: 0,
              organization: userData?.organization?._id || '',
              marca: { nombre: 'Venta Rápida', _id: 'varios' } as any,
            } as any as Product;
            
            const todosLosProductos = [productoVarios, ...productosData];
            setProductos(todosLosProductos)
            setFilteredProductos(todosLosProductos)
          })
      } else {
        const cachedProduct = productos.find((p) => p.codigo?.toLowerCase() === searchValue)

        if (cachedProduct) {
          handleProductSelect(cachedProduct)
          setSearchTerm("")
        } else if (searchValue.length >= 3) {
          api
            .get(`/api/productos?sucursal=${selectedSucursal}&searchTerm=${searchValue}&limit=20`)
            .then((res) => {
              const productosData: Product[] = res.data.productos || []
              const exactProduct = productosData.find((p) => p.codigo?.toLowerCase() === searchValue)

              if (exactProduct) {
                handleProductSelect(exactProduct)
                setProductos((prev) => [...prev, exactProduct])
                setSearchTerm("")
              } else {
                // AGREGAR: No incluir "Varios" en resultados de búsqueda específica
                setFilteredProductos(productosData)
              }
            })
        }
      }
    }, 600)
  }

  const handleProductSelect = (producto: Product) => {
    // Verificar primero si hay caja abierta
    if (!cajaAbierta) {
      setMessageContent("Debes abrir una caja antes de realizar ventas. Ve a 'Gestión de Caja' para abrir una.");
      setMessageType("warning");
      setIsMessageModalOpen(true);
      return;
    }

    if (!selectedSucursal) {
      setMessageContent("Por favor, selecciona una sucursal primero.");
      setMessageType("aviso");
      setIsMessageModalOpen(true);
      return;
    }

    if (!activeOrder) {
      const newPrincipal = createPrincipalOrder()
      setOrders([newPrincipal])
      setActiveOrderId(newPrincipal.id)
      return 
    }

    // NUEVO: Detectar si es el producto "Varios"
    if (producto.codigo === 'VARIOS' || producto.nombre === 'Varios') {
      setIsVariosModalOpen(true);
      return;
    }

    if ((producto.cantidad ?? 0) <= 0) {
      setMessageContent(`El producto "${producto.nombre}" no tiene stock disponible.`)
      setMessageType("aviso")
      setIsMessageModalOpen(true)
      return
    }

    const existingProduct = activeOrder.selectedProducts.find((p) => p.producto._id === producto._id)

    if (esPesable(producto)) {
      setSelectedPesoProduct(producto)
      setIsPesoModalOpen(true)
      return
    }

    if (producto.unidadMedida === "unidad" && existingProduct) {
      const nuevaCantidad = Number(existingProduct.cantidad) + 1
      if (nuevaCantidad > (producto.cantidad ?? 0)) {
        setMessageContent(
          `No puedes agregar más unidades. Solo hay ${
            producto.cantidad ?? 0
          } disponibles para "${producto.nombre}".`
        )
        setMessageType("aviso")
        setIsMessageModalOpen(true)
        return
      }
    }
    let updatedProducts: SelectedProduct[]
    if (esPesable(producto) && existingProduct) {
      setSelectedPesoProduct(producto)
      setIsPesoModalOpen(true)
    } else {
      updatedProducts = existingProduct
        ? activeOrder.selectedProducts.map((p) =>
            p.producto._id === producto._id ? { ...p, cantidad: Number(p.cantidad) + 1 } : p
          )
        : [...activeOrder.selectedProducts, { producto, cantidad: 1 }]
      updateSubtotal(updatedProducts, (newSubtotal, newIva) => {
        updateActiveOrder({ selectedProducts: updatedProducts, subtotal: newSubtotal, iva: newIva })
      })
    }
  }

  const handleSaveVarios = useCallback((nombre: string, valor: number) => {
    if (!activeOrder) return;

    // Crear un producto temporal para "Varios"
    const productoVarios: Product = {
      _id: `varios-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Más único
      nombre: nombre,
      codigo: 'VARIOS',
      valorVentaTotal: valor,
      valorCompra: 0,
      cantidad: 999999,
      unidadMedida: 'unidad',
      ivaPorcentaje: 0,
      margenGanancia: 0,
      valorVenta: valor,
      organization: userData?.organization?._id || '',
      esVarios: true,
    } as any as Product;

    const updatedProducts = [
      ...activeOrder.selectedProducts,
      { producto: productoVarios, cantidad: 1 }
    ];

    updateSubtotal(updatedProducts, (newSubtotal, newIva) => {
      updateActiveOrder({ selectedProducts: updatedProducts, subtotal: newSubtotal, iva: newIva });
    });
  }, [activeOrder, updateActiveOrder, userData, updateSubtotal]);

  const handleCantidadChange = (index: number, cantidad: string) => {
    if (!activeOrder) return
    const updatedProducts = [...activeOrder.selectedProducts]
    const productoSeleccionado = updatedProducts[index].producto

    const cantidadNueva = esPesable(productoSeleccionado)
      ? Number.parseFloat(cantidad) || 0
      : Number.parseInt(cantidad) || 1

    if (productoSeleccionado.cantidad && cantidadNueva > productoSeleccionado.cantidad) {
      setMessageContent(`No hay suficiente stock para el producto: ${productoSeleccionado.nombre}`)
      setMessageType("warning")
      setIsMessageModalOpen(true)
      return
    }

    updatedProducts[index].cantidad = cantidadNueva
    updateSubtotal(updatedProducts, (newSubtotal, newIva) => {
      updateActiveOrder({ selectedProducts: updatedProducts, subtotal: newSubtotal, iva: newIva })
    })
  }

  const handleDeleteProduct = (index: number) => {
    if (!activeOrder) return
    const updatedProducts = [...activeOrder.selectedProducts]
    updatedProducts.splice(index, 1)
    updateSubtotal(updatedProducts, (newSubtotal, newIva) => {
      updateActiveOrder({ selectedProducts: updatedProducts, subtotal: newSubtotal, iva: newIva })
    })
  }

  const handleSubmitSale = () => {
    setIsConfirmationModalOpen(true)
  }

  const closePaymentModal = () => setIsPaymentModalOpen(false)

  const refetchProductos = (productosVendidos: { producto: { _id: string } }[]) => {
    const idsVendidos = productosVendidos.map((pv) => pv.producto._id)
    const hayCoincidencia = productos.some((p) => idsVendidos.includes(p._id))

    if (hayCoincidencia && selectedSucursal) {
      api
        .get(`/api/productos?sucursal=${selectedSucursal}&limit=10`)
        .then((res) => {
          const productosData: Product[] = res.data.productos || []
          
          // AGREGAR: Incluir "Varios" al refrescar
          const productoVarios: Product = {
            _id: 'varios-especial',
            nombre: 'Varios',
            codigo: 'VARIOS',
            valorVentaTotal: 0,
            valorCompra: 0,
            cantidad: 999999,
            unidadMedida: 'unidad',
            ivaPorcentaje: 0,
            margenGanancia: 0,
            valorVenta: 0,
            organization: userData?.organization?._id || '',
            marca: { nombre: 'Venta Rápida', _id: 'varios' } as any,
          } as any as Product;
          
          const todosLosProductos = [productoVarios, ...productosData];
          setProductos(todosLosProductos)
          setFilteredProductos(todosLosProductos)
        })
        .catch((error) => console.error("Error al refrescar productos:", error))
    }
  }

  const handleConfirmSale = () => {
    if (!activeOrder || !selectedSucursal) {
      setMessageContent("No hay una orden activa o sucursal seleccionada para procesar la venta.")
      setMessageType("error")
      setIsMessageModalOpen(true)
      return
    }

    const dineroRecibidoNumeric =
      Number.parseInt(activeOrder.dineroRecibido.toString().replace(/[^\d]/g, ""), 10) || 0
    const totalConDescuento =
      calcularSubtotalConDescuentos(activeOrder.selectedProducts) *
      (1 - (Number.parseFloat(activeOrder.descuentoPorcentaje as string) || 0) / 100)
    const cambioCalculado = dineroRecibidoNumeric - Math.round(totalConDescuento)
    const totalConDescuentoRedondeado = Math.round(totalConDescuento)

    const sucursalCompleta = sucursales.find((s) => s._id === selectedSucursal)

    if (!sucursalCompleta) {
      setMessageContent("La sucursal seleccionada no es válida.")
      setMessageType("error")
      setIsMessageModalOpen(true)
      return
    }

    const saleData = {
      productos: activeOrder.selectedProducts.map((sp) => ({
        producto: {
          _id: sp.producto._id,
          nombre: sp.producto.nombre,
          unidadMedida: sp.producto.unidadMedida,
          valorVenta: sp.producto.valorVentaTotal,
        },
        cantidad: sp.cantidad,
        valor: Math.round(
          Number(sp.cantidad) * sp.producto.valorVentaTotal * (1 - (Number(sp.descuento) || 0) / 100)
        ),
        descuento: sp.descuento ?? 0,
      })),
      totalVenta: totalConDescuentoRedondeado,
      dineroRecibido: dineroRecibidoNumeric,
      cambio: cambioCalculado,
      metodoPago: activeOrder.metodoPago,
      descuentoPorcentaje: Number.parseFloat(activeOrder.descuentoPorcentaje as string) || 0,
      sucursal: sucursalCompleta,
      fechaVenta: new Date().toLocaleString("sv-SE", { timeZone: "America/Bogota" }).replace(" ", "T"),
    }

    apiVentas
      .post("/api/sales/add", saleData)
      .then((response) => {
        setFactura(response.data.venta)
        setIsPrintModalOpen(true)
        refetchProductos(saleData.productos)
      })
      .catch((error) => {
        setMessageContent("Hubo un error al registrar la venta.")
        setMessageType("error")
        setIsMessageModalOpen(true)
        console.error(error)
      })
      .finally(() => {
        setIsConfirmationModalOpen(false)
      })
  }

  const closePrintModal = () => {
    setIsPrintModalOpen(false)
    setFactura(null)
    setMessageContent("Venta registrada exitosamente.")
    setMessageType("success")
    setIsMessageModalOpen(true)

    if (activeOrderId) {
      closeOrder(activeOrderId)
    }
    setSearchTerm("")
    setFilteredProductos(productos)
  }

  const handleCloseMessageModal = () => {
    setIsMessageModalOpen(false)
  }

  const esPesable = (producto: Product) => ["kg", "gramo", "litro", "metro"].includes(producto.unidadMedida || "")

  const handleProductDiscountChange = (index: number, value: string) => {
    if (!activeOrder) return
    const updated = [...activeOrder.selectedProducts]
    if (value === "") {
      updated[index].descuento = value
    } else {
      const numeric = Number(value)
      if (!isNaN(numeric)) {
        updated[index].descuento = Math.min(Math.max(numeric, 0), 100)
      }
    }
    updateSubtotal(updated, (newSubtotal, newIva) => {
      updateActiveOrder({ selectedProducts: updated, subtotal: newSubtotal, iva: newIva })
    })
  }

  const calcularSubtotalConDescuentos = (productos: SelectedProduct[]) => {
    const total = productos.reduce((sum, sp) => {
      const cantidad = Number(sp.cantidad) || 0
      const base = cantidad * sp.producto.valorVentaTotal
      const desc = (Number(sp.descuento) || 0) / 100
      return sum + base * (1 - desc)
    }, 0)
    return Math.round(total)
  }

  const handleSendInvoiceEmail = async (data: InvoiceRecipientInfo) => {
    if (!factura) {
      setMessageContent("No hay una factura generada para enviar.")
      setMessageType("error")
      setIsMessageModalOpen(true)
      // Usamos return en lugar de throw para no mostrar dos errores (el del modal y este)
      return; 
    }

    try {
      // Envía todos los datos nuevos al backend
      await apiVentas.post("/api/sales/send-invoice", {
        saleId: factura._id,
        ...data // Esto incluye email, y opcionalmente customerName y customerDocument
      })
      setMessageContent(`Factura enviada exitosamente a ${data.recipientEmail}.`)
      setMessageType("success")
      setIsMessageModalOpen(true)
    } catch (error: any) {
      const errorMessage = error.response?.data?.msg || "Error al enviar la factura por correo."
      setMessageContent(errorMessage)
      setMessageType("error")
      setIsMessageModalOpen(true)
      // Mantenemos el throw para que el modal sepa que hubo un error y no se cierre
      throw new Error(errorMessage) 
    }
  }

  return (
    <div className="flex flex-col h-full w-full bg-gray-50 overflow-hidden">
        {isLoading && <Loader />}

        {/* Banner de estado de caja - Ocupa todo el ancho */}
        <div className="w-full">
          {!cajaAbierta ? (
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-3 py-2 flex items-center justify-between shadow-inner">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 flex items-center justify-center animate-pulse flex-shrink-0">
                  <i className="fas fa-lock text-white text-sm"></i>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-bold text-white text-xs sm:text-sm truncate flex items-center gap-1.5">
                    <i className="fas fa-exclamation-triangle text-white"></i>
                    Caja Cerrada
                  </span>
                  <span className="text-white/90 text-[10px] sm:text-xs hidden sm:block">
                    Abre una caja para vender
                  </span>
                </div>
              </div>
              <a 
                href="/caja" 
                className="bg-white text-red-600 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg font-bold hover:bg-red-50 
                          transition-all shadow-md hover:shadow-lg flex items-center gap-1.5 text-xs sm:text-sm flex-shrink-0"
              >
                <i className="fas fa-unlock-alt"></i>
                <span>Abrir</span>
              </a>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-3 py-2 flex items-center justify-between gap-2 shadow-inner">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-check-circle text-white text-sm"></i>
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="font-bold text-white text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                      <i className="fas fa-check-double text-white"></i>
                      Abierta
                    </span>
                    <span className="bg-white/20 text-white px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold truncate">
                      {cajaActiva?.cajero?.nombre || 'Sin sucursal'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-white/95 text-[10px] sm:text-xs overflow-hidden">
                    {cajaActiva?.usuario && (
                      <span className="flex items-center gap-1 truncate">
                        <i className="fas fa-user text-white/80 text-[9px] sm:text-xs"></i>
                        <span className="font-medium truncate">{cajaActiva.usuario.username || 'Usuario'}</span>
                      </span>
                    )}
                    {cajaActiva?.fechaApertura && (
                      <span className="hidden sm:flex items-center gap-1">
                        <i className="fas fa-calendar-alt text-white/80"></i>
                        <span className="whitespace-nowrap">
                          {new Date(cajaActiva.fechaApertura).toLocaleDateString('es-CO', {
                            day: '2-digit',
                            month: 'short'
                          })}
                        </span>
                        <i className="fas fa-clock text-white/80"></i>
                        <span className="whitespace-nowrap">
                          {new Date(cajaActiva.fechaApertura).toLocaleTimeString('es-CO', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <a 
                href="/caja?action=close" 
                className="bg-white text-green-600 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg font-bold hover:bg-green-50 
                          transition-all shadow-md hover:shadow-lg flex items-center gap-1.5 text-xs sm:text-sm flex-shrink-0"
              >
                <i className="fas fa-times-circle"></i>
                <span>Cerrar</span>
              </a>
            </div>
          )}
        </div>

        {/* Contenedor de paneles izquierdo y derecho */}
        <div className={`flex flex-1 overflow-hidden relative ${!cajaAbierta ? 'pointer-events-none' : ''}`}>
          {/* Overlay oscuro cuando la caja está cerrada */}
          {!cajaAbierta && (
            <div className="absolute inset-0 bg-black/40 z-30 backdrop-blur-[2px]"></div>
          )}

          {/* Panel Izquierdo - Productos */}
          <div
            className={`flex flex-col h-full ${
              isRightPaneVisible && window.innerWidth > 768 ? "w-1/2 lg:w-3/5" : "w-full"
            } transition-all duration-300 ease-in-out bg-white shadow-lg relative`}
            style={{
              display: window.innerWidth <= 768 && isRightPaneVisible ? "none" : "flex",
            }}
          >
          {/* Header de búsqueda con banner integrado */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
            {/* Barra de búsqueda */}
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <select
                  id="sucursal"
                  value={selectedSucursal || ""}
                  onChange={handleSucursalChange}
                  disabled={cajaAbierta}
                  className={`px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white 
                    focus:outline-none focus:ring-2 focus:ring-white/50 w-full md:w-1/3
                    ${cajaAbierta ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  <option value="" disabled className="text-gray-800">
                    Seleccione una sucursal
                  </option>
                  {cajaAbierta && cajaActiva?.sucursal ? (
                    <option key={cajaActiva.sucursal._id} value={cajaActiva.sucursal._id} className="text-gray-800">
                      {cajaActiva.sucursal.nombre} (Caja Abierta)
                    </option>
                  ) : (
                    sucursales.map((sucursal) => (
                      <option key={sucursal._id} value={sucursal._id} className="text-gray-800">
                        {sucursal.nombre}
                      </option>
                    ))
                  )}
                </select>

                <div className="relative flex-1">
                  <input
                    type="text"
                    id="search"
                    placeholder="Buscar por nombre o código"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full px-4 py-2 pl-10 pr-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 
                              text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                    disabled={!selectedSucursal}
                    ref={searchInputRef}
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70"></i>
                  
                  {/* Botón del escáner QR */}
                  <button
                    onClick={() => setIsQRScannerOpen(true)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white hover:text-green-300"
                    title="Escanear código QR"
                  >
                    <i className="fas fa-qrcode text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {selectedSucursal ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filteredProductos.map((producto) => {
                  const selectedProduct = activeOrder?.selectedProducts.find(
                    (p) => p.producto._id === producto._id
                  )
                  const isSelected = !!selectedProduct
                  const selectionCount = selectedProduct ? selectedProduct.cantidad : 0
                  const isVarios = producto.codigo === 'VARIOS'

                  const rolesConPermisoEdicion = ['admin', 'administrador', 'gerente']
                  const canEditProducts = rolesConPermisoEdicion.includes(userData?.role?.toLowerCase() || '')

                  return (
                  <div
                    key={producto._id}
                    className={`relative flex flex-col p-3 rounded-xl shadow-sm cursor-pointer transition-all h-[190px]
                                ${isVarios ? 'bg-gradient-tos-br from-blue-50 to-blue-100 border-2 border-blue-500' : ''}
                                ${
                                  isSelected && !isVarios
                                    ? "bg-blue-100 border-2 border-blue-500 shadow-md transform scale-[1.02]"
                                    : !isVarios ? "bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-300" : ""
                                }`}
                  >
                    {/* Botón de edición rápida - Solo visible para admin/gerente */}
                    {!isVarios && canEditProducts && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickEdit(producto);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full 
                                  bg-blue-500 text-white hover:bg-blue-600 transition-all shadow-md 
                                  hover:shadow-lg z-10 group"
                        title="Editar producto"
                      >
                        <i className="fas fa-edit text-xs group-hover:scale-110 transition-transform"></i>
                      </button>
                    )}

                    {/* Badge de selección */}
                    {isSelected && (
                      <div
                        className={`absolute -top-2 -left-2 w-7 h-7 flex items-center justify-center 
                                    ${isVarios ? 'bg-blue-600' : 'bg-blue-600'} text-white rounded-full text-sm font-bold shadow-md`}
                      >
                        {selectionCount}
                      </div>
                    )}

                    {/* Contenido clickeable para agregar al carrito */}
                    <div 
                      onClick={() => handleProductSelect(producto)}
                      className="flex flex-col h-full"
                    >
                      <div className="mb-2">
                        <div className="flex items-center gap-1 mb-1">
                          {isVarios ? (
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-600 text-white">
                              <i className="fas fa-bolt text-xs"></i>
                            </div>
                          ) : (
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
                              <i className="fas fa-tag text-xs"></i>
                            </div>
                          )}
                          {producto.marca && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              isVarios ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {producto.marca.nombre}
                            </span>
                          )}
                        </div>
                        <p className={`font-medium text-sm leading-tight break-words whitespace-normal ${
                          isVarios ? 'text-blue-800' : 'text-gray-800'
                        }`}>
                          {producto.nombre}
                        </p>
                        <p className={`text-xs mt-1 ${isVarios ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                          {isVarios ? 'Click para agregar producto rápido' : (producto.codigo ? `Código: ${producto.codigo}` : "Sin código")}
                        </p>
                      </div>

                      {!isVarios && (
                        <span className="text-lg font-bold text-blue-700 mt-auto">
                          ${producto.valorVentaTotal.toLocaleString()}
                          <span className="text-xs text-gray-500 ml-1">/ {producto.unidadMedida}</span>
                        </span>
                      )}

                      {isVarios && (
                        <span className="text-sm font-bold text-blue-700 mt-auto">
                          Venta Rápida
                        </span>
                      )}
                    </div>
                  </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <img
                  src="/sliders/stock.svg"
                  alt="Selecciona una sucursal"
                  className="w-60 h-60 mb-4 opacity-60 grayscale"
                />
                <p className="text-lg text-gray-500 font-medium">
                  Selecciona una sucursal para comenzar a vender
                </p>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-gray-200 bg-gray-100">
            <div className="flex justify-around">
              <div className="flex items-center gap-1">
                <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">F8</span>
                <span className="text-sm text-gray-600">Buscar</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">ESC</span>
                <span className="text-sm text-gray-600">Cancelar</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">
                  SHIFT
                </span>
                <span className="text-sm text-gray-600">Pagar</span>
              </div>
            </div>
          </div>
        </div>
        
        {window.innerWidth > 768 && (
          <div className="relative z-10" style={{ width: 0 }}>
            <button
              onClick={toggleRightPane}
              className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2
                        bg-blue-600 text-white rounded-full shadow-lg p-3 border border-blue-700
                        hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            >
              <i className={`fas ${isRightPaneVisible ? "fa-chevron-right" : "fa-chevron-left"}`}></i>
            </button>
          </div>
        )}

        {window.innerWidth <= 768 && !isRightPaneVisible && (
          <button
            onClick={toggleRightPane}
            className="fixed bottom-16 right-6 z-20 bg-blue-600 text-white rounded-full shadow-lg p-4 border border-blue-700
                        hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          >
            <i className="fas fa-shopping-cart"></i>
            {activeOrder && activeOrder.selectedProducts.length > 0 && (
              <span
                className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center 
                              bg-red-500 text-white text-xs rounded-full font-bold"
              >
                {activeOrder.selectedProducts.length}
              </span>
            )}
          </button>
        )}

        <div
          className={`flex flex-col h-full ${
            isRightPaneVisible ? (window.innerWidth <= 768 ? "w-full" : "w-1/2 lg:w-2/5") : "w-0"
          } transition-all duration-300 ease-in-out bg-gray-50 overflow-hidden`}
          style={{
            display: isRightPaneVisible ? "flex" : "none",
          }}
        >
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-600 text-white">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Carrito de Venta</h2>
              {window.innerWidth <= 768 && (
                <button
                  onClick={toggleRightPane}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2 border-b border-white/20 pb-2 overflow-x-auto">
              {orders.map((order) => (
                <div
                  key={order.id}
                  onClick={() => setActiveOrderId(order.id)}
                  className={`flex items-center gap-2 whitespace-nowrap cursor-pointer px-3 py-2 rounded-t-lg transition-colors ${
                    order.id === activeOrderId
                      ? "bg-gray-50 text-blue-700 font-bold"
                      : "text-white hover:bg-white/20"
                  }`}
                >
                  <span>{order.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      closeOrder(order.id)
                    }}
                    className="w-5 h-5 rounded-full hover:bg-red-500 hover:text-white flex items-center justify-center"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              ))}
              <button
                onClick={() => setIsNameModalOpen(true)}
                className="ml-2 flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors"
                title="Crear nuevo pedido"
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {activeOrder && activeOrder.selectedProducts.length > 0 ? (
              <div className="space-y-3">
                {activeOrder.selectedProducts.map((sp, index) => (
                  <div
                    key={`${sp.producto._id}-${index}`}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-col md:flex-row gap-3"
                  >
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800">{sp.producto.nombre}</h3>
                      <div className="text-sm text-gray-500 mt-1">
                        {`Venta por ${sp.producto.unidadMedida}: ${sp.producto.valorVentaTotal.toLocaleString()}`}
                      </div>
                      <div className="font-medium text-blue-700 mt-1">
                        Subtotal: $
                        {Math.round(
                          Number(sp.cantidad) *
                            sp.producto.valorVentaTotal *
                            (1 - (Number(sp.descuento) || 0) / 100)
                        ).toLocaleString()}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 carrito-descuento-activo">
                        <label className="text-sm font-medium text-gray-700">Desc (%)</label>
                        <input
                          type="number"
                          value={sp.descuento?.toString() ?? ""}
                          onFocus={() => setIsRightPaneVisible(true)}
                          min={0}
                          max={100}
                          onChange={(e) => handleProductDiscountChange(index, e.target.value)}
                          placeholder="0"
                          className="w-14 px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>                                 
                      
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">
                          {esPesable(sp.producto) ? `${sp.producto.unidadMedida}(s):` : 'Cant:'}
                        </label>
                        {!esPesable(sp.producto) && (
                          <button
                            onClick={() => handleCantidadChange(index, String(Number(sp.cantidad) - 1))}
                            className="w-8 h-8 flex items-center justify-center rounded-l-lg bg-gray-200 text-gray-700 
                                            hover:bg-gray-300 focus:outline-none"
                            disabled={Number(sp.cantidad) <= 1}
                          >
                            <i className="fas fa-minus"></i>
                          </button>
                        )}
                        <input
                          type="number"
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-center font-medium"
                          value={sp.cantidad.toString()}
                          onChange={(e) => handleCantidadChange(index, e.target.value)}
                          onFocus={(e) => {
                            setIsRightPaneVisible(true)
                            e.target.select() // Selecciona todo el texto al hacer clic
                          }}
                          min={esPesable(sp.producto) ? "0.01" : "1"}
                          step={esPesable(sp.producto) ? "0.01" : "1"}
                          placeholder={esPesable(sp.producto) ? "Peso" : "Cant"}
                        />
                        {!esPesable(sp.producto) && (
                          <button
                            onClick={() => handleCantidadChange(index, String(Number(sp.cantidad) + 1))}
                            className="w-8 h-8 flex items-center justify-center rounded-r-lg bg-gray-200 text-gray-700 
                                            hover:bg-gray-300 focus:outline-none"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteProduct(index)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-600 
                                        hover:bg-red-200 focus:outline-none"
                      >
                        <i className="fa-regular fa-trash-can"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <i className="fas fa-cart-plus text-6xl text-gray-300 mb-4"></i>
                <p className="text-lg text-gray-500">El carrito está vacío</p>
                <p className="text-sm text-gray-400 mt-2">
                  Selecciona productos del catálogo para comenzar
                </p>
              </div>
            )}
          </div>
          
          {activeOrder && (
            <div className="p-4 border-t border-gray-200 bg-white shadow-md">
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <p className="text-sm text-gray-600">Subtotal:</p>
                    <p className="text-xl font-bold text-gray-800">
                      ${Math.round(activeOrder.subtotal).toLocaleString()}
                    </p>
                  </div>

                  {userData?.organization?.responsableIVA && (
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <p className="text-sm text-gray-600">IVA (19.00%):</p>
                      <p className="text-xl font-bold text-gray-800">
                        ${Math.round(activeOrder.iva).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-2">
                  <button
                    onClick={handleClearProducts}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg
                                    hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    <i className="fa-solid fa-eraser"></i>
                    <span>Limpiar</span>
                  </button>

                  <button
                    onClick={openPaymentModal}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 
                                    text-white rounded-lg font-bold shadow-md hover:from-blue-700 hover:to-blue-800 
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 
                                    disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-blue-700"
                    disabled={activeOrder.selectedProducts.length === 0}
                  >
                    <i className="fa-solid fa-money-bill"></i>
                    <span>Vender</span>
                    <span className="text-white/90">
                      ${Math.round(activeOrder.subtotal + activeOrder.iva).toLocaleString()}
                    </span>
                  </button>
                </div>

                {/* 
                <div className="flex justify-between items-center mt-1 px-1">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{activeOrder.selectedProducts.length}</span>{" "}
                    productos seleccionados
                  </p>
                </div>
                */}
              </div>
            </div>
          )}
        </div>
      </div>

        {/* --- MODALES --- */}

        <QuickEditProductModal
          isOpen={isQuickEditModalOpen}
          onClose={() => {
            setIsQuickEditModalOpen(false)
            setProductToEdit(null)
          }}
          product={productToEdit}
          onSaveSuccess={handleQuickEditSuccess}
        />

        <VariosModal
          isOpen={isVariosModalOpen}
          onClose={() => setIsVariosModalOpen(false)}
          onSave={handleSaveVarios}
        />

        <CreateOrderModal
            isOpen={isNameModalOpen}
            onClose={() => setIsNameModalOpen(false)}
            onSave={handleSaveNewOrder}
            defaultName={`Pedido ${orders.length + 1}`}
        />

        <WeightModal
            isOpen={isPesoModalOpen}
            productName={selectedPesoProduct?.nombre || ""}
            pricePerKg={selectedPesoProduct?.valorVentaTotal || 0}
            availableWeight={selectedPesoProduct?.cantidad || 0}
            unitLabel={selectedPesoProduct?.unidadMedida || "unidad"}
            onClose={() => setIsPesoModalOpen(false)}
            onSave={(weight) => {
            if (selectedPesoProduct && activeOrder) {
                const updatedProducts = [...activeOrder.selectedProducts]
                const existingProduct = updatedProducts.find((p) => p.producto._id === selectedPesoProduct._id)

                if (existingProduct) {
                existingProduct.cantidad = weight
                } else {
                updatedProducts.push({
                    producto: selectedPesoProduct,
                    cantidad: weight,
                })
                }

                updateSubtotal(updatedProducts, (newSubtotal, newIva) => {
                updateActiveOrder({ selectedProducts: updatedProducts, subtotal: newSubtotal, iva: newIva })
                })
                setIsPesoModalOpen(false)
            }
            }}
        />

        {activeOrder && (
            <PaymentModal
            total={calcularSubtotalConDescuentos(activeOrder.selectedProducts)}
            descuentoInicial={Number.parseFloat(activeOrder.descuentoPorcentaje as string) || 0}
            isOpen={isPaymentModalOpen}
            onClose={closePaymentModal}
            onSubmit={(method, amount, discount) => {
                updateActiveOrder({
                metodoPago: method,
                dineroRecibido: amount.toString(),
                descuentoPorcentaje: discount.toString(),
                })
                setTimeout(handleSubmitSale, 0)
            }}
            />
        )}

        <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            title="Confirmación"
            message="¿Está seguro de que desea guardar la venta?"
            onConfirm={handleConfirmSale}
            onCancel={() => setIsConfirmationModalOpen(false)}
        />

        <MessageModal
            isOpen={isMessageModalOpen}
            message={messageContent}
            messageType={messageType}
            onClose={handleCloseMessageModal}
        />

        <PrintModal 
          factura={factura} 
          isOpen={isPrintModalOpen} 
          onClose={closePrintModal}
          onSendEmail={handleSendInvoiceEmail}
        />

        {isQRScannerOpen && (
          <QRScanner
            onScanSuccess={(code) => {
              setIsQRScannerOpen(false)
              setSearchTerm(code)
              handleSearchChange({ target: { value: code } } as React.ChangeEvent<HTMLInputElement>)
            }}
            onClose={() => setIsQRScannerOpen(false)}
          />
        )}
    </div>
  )
}

export default ManageSales